import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getTitleFromSlug, extractJobIdFromSlug } from '../utils/slugify';

const NavigationContext = createContext();

const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

const NavigationProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [quickAccessItems, setQuickAccessItems] = useState([]);
  const [recentlyVisited, setRecentlyVisited] = useState([]);
  const [navigationPreferences, setNavigationPreferences] = useState({
    showBreadcrumbs: true,
    showQuickActions: true,
    compactMode: false,
    sidebarCollapsed: false
  });

  // Track navigation history
  useEffect(() => {
    const currentPage = {
      path: location.pathname,
      timestamp: Date.now(),
      title: getPageTitle(location.pathname)
    };

    setNavigationHistory(prev => {
      const filtered = prev.filter(item => item.path !== location.pathname);
      return [currentPage, ...filtered].slice(0, 10); // Keep last 10 pages
    });

    // Update recently visited (exclude current page)
    setRecentlyVisited(prev => {
      const filtered = prev.filter(item => item.path !== location.pathname);
      return [currentPage, ...filtered].slice(0, 5); // Keep last 5 visited
    });
  }, [location.pathname]);

  // Initialize quick access items based on user role, merging with persisted custom items
  useEffect(() => {
    if (user) {
      const roleItems = getQuickAccessItems(user.role);
      let savedCustomItems = [];
      try {
        const saved = localStorage.getItem('quickAccessCustomItems');
        if (saved) savedCustomItems = JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to load custom quick access items:', e);
      }
      // Merge role-based items with saved custom items (avoid duplicates)
      const merged = [
        ...roleItems,
        ...savedCustomItems.filter(ci => !roleItems.find(ri => ri.id === ci.id))
      ];
      setQuickAccessItems(merged);
    }
  }, [user?.id, user?.role]);

  // Get page title from path
  const getPageTitle = (path) => {
    const titleMap = {
      '/va-dashboard': 'VA Dashboard',
      '/employer-dashboard': 'Employer Dashboard',
      '/job-search': 'Job Search',
      '/job-posting': 'Post a Job',
      '/candidate-search': 'Find Candidates',
      '/messages-communication': 'Messages',
      '/messages': 'Messages',
      '/interviews': 'Interviews',
      '/interview-management': 'Interview Management',
      '/analytics': 'Analytics',
      '/advanced-analytics': 'Advanced Analytics',
      '/va-profile': 'My Profile',
      '/my-contracts': 'My Contracts',
      '/va-profile-settings': 'Profile Settings',
      '/employer-profile-settings': 'Company Settings',
      '/skills-assessment': 'Skills Assessment',
      '/team-management': 'Team Management',
      '/auto-matching': 'AI Matching',
      '/enterprise-features': 'Enterprise Features',
      '/contact-va': 'Contact VA',
      '/job-edit': 'Edit Job',
      '/application-review': 'Application Review',
      '/va-profile-view': 'VA Profile',
      '/interview-schedule': 'Schedule Interview',
      // Admin routes
      '/admin-moderation': 'Admin Panel',
      '/admin-moderation/skills-assessment': 'Skills Assessment Admin'
    };

    // Handle dynamic routes
    if (path.startsWith('/job-details/')) {
      const pathSegments = path.split('/');
      const slug = pathSegments[2];
      
      if (slug && extractJobIdFromSlug(slug)) {
        const jobTitle = getTitleFromSlug(slug);
        return jobTitle || 'Job Details';
      }
      
      return 'Job Details';
    }

    return titleMap[path] || 'Page';
  };

  // Get quick access items based on user role
  const getQuickAccessItems = (role) => {
    if (role === 'va') {
      return [
        {
          id: 'search-jobs',
          label: 'Search Jobs',
          icon: 'Search',
          path: '/job-search',
          category: 'primary'
        },
        {
          id: 'my-applications',
          label: 'My Applications',
          icon: 'FileText',
          path: '/applications',
          category: 'primary'
        },
        {
          id: 'update-profile',
          label: 'Update Profile',
          icon: 'User',
          path: '/va-profile',
          category: 'secondary'
        },
        {
          id: 'skills-test',
          label: 'Take Skills Test',
          icon: 'Award',
          path: '/skills-assessment',
          category: 'secondary'
        },
        {
          id: 'view-analytics',
          label: 'View Analytics',
          icon: 'BarChart3',
          path: '/analytics',
          category: 'secondary'
        }
      ];
    } else if (role === 'employer') {
      return [
        {
          id: 'post-job',
          label: 'Post New Job',
          icon: 'Plus',
          path: '/job-posting',
          category: 'primary'
        },
        {
          id: 'find-candidates',
          label: 'Find Candidates',
          icon: 'Users',
          path: '/candidate-search',
          category: 'primary'
        },
        {
          id: 'manage-jobs',
          label: 'Manage Jobs',
          icon: 'Briefcase',
          path: '/employer-dashboard',
          category: 'secondary'
        },
        {
          id: 'schedule-interview',
          label: 'Schedule Interview',
          icon: 'Calendar',
          path: '/interview-management',
          category: 'secondary'
        },
        {
          id: 'view-reports',
          label: 'View Reports',
          icon: 'BarChart3',
          path: '/advanced-analytics',
          category: 'secondary'
        }
      ];
    }
    return [];
  };

  // Navigation utilities
  const goBack = () => {
    if (navigationHistory.length > 1) {
      const previousPage = navigationHistory[1];
      navigate(previousPage.path);
    } else {
      navigate(-1);
    }
  };

  const goToPage = (path, options = {}) => {
    navigate(path, options);
  };

  const addToQuickAccess = (item) => {
    setQuickAccessItems(prev => {
      const exists = prev.find(existing => existing.id === item.id);
      if (exists) return prev;
      const updated = [...prev, { ...item, category: 'custom' }].slice(0, 8);
      const customOnly = updated.filter(i => i.category === 'custom');
      localStorage.setItem('quickAccessCustomItems', JSON.stringify(customOnly));
      return updated;
    });
  };

  const removeFromQuickAccess = (itemId) => {
    setQuickAccessItems(prev => {
      const updated = prev.filter(item => item.id !== itemId || item.category !== 'custom');
      const customOnly = updated.filter(i => i.category === 'custom');
      localStorage.setItem('quickAccessCustomItems', JSON.stringify(customOnly));
      return updated;
    });
  };

  const updateNavigationPreferences = (newPreferences) => {
    setNavigationPreferences(prev => {
      const merged = { ...prev, ...newPreferences };
      localStorage.setItem('navigationPreferences', JSON.stringify(merged));
      return merged;
    });
  };

  // Load preferences from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('navigationPreferences');
    if (saved) {
      try {
        const preferences = JSON.parse(saved);
        setNavigationPreferences(prev => ({ ...prev, ...preferences }));
      } catch (error) {
        console.warn('Failed to load navigation preferences:', error);
      }
    }
  }, []);

  // Get contextual navigation suggestions
  const getNavigationSuggestions = () => {
    const currentPath = location.pathname;
    const suggestions = [];

    if (user?.role === 'va') {
      if (currentPath === '/va-dashboard') {
        suggestions.push(
          { label: 'Find New Jobs', path: '/job-search', reason: 'Discover opportunities' },
          { label: 'Complete Profile', path: '/va-profile', reason: 'Improve visibility' },
          { label: 'Take Skills Assessment', path: '/skills-assessment', reason: 'Boost credibility' }
        );
      } else if (currentPath === '/job-search') {
        suggestions.push(
          { label: 'View My Applications', path: '/applications', reason: 'Track progress' },
          { label: 'Update Profile', path: '/va-profile', reason: 'Stand out more' }
        );
      } else if (currentPath.startsWith('/job-details')) {
        suggestions.push(
          { label: 'Similar Jobs', path: '/job-search', reason: 'Find more opportunities' },
          { label: 'My Applications', path: '/applications', reason: 'Track applications' }
        );
      }
    } else if (user?.role === 'employer') {
      if (currentPath === '/employer-dashboard') {
        suggestions.push(
          { label: 'Post New Job', path: '/job-posting', reason: 'Find talent faster' },
          { label: 'Search Candidates', path: '/candidate-search', reason: 'Proactive hiring' },
          { label: 'View Analytics', path: '/advanced-analytics', reason: 'Optimize hiring' }
        );
      } else if (currentPath === '/job-posting') {
        suggestions.push(
          { label: 'Find Candidates', path: '/candidate-search', reason: 'Proactive outreach' },
          { label: 'Manage Posted Jobs', path: '/employer-dashboard', reason: 'Track performance' }
        );
      }
    }

    return suggestions.slice(0, 3); // Max 3 suggestions
  };

  const value = {
    // State
    navigationHistory,
    quickAccessItems,
    recentlyVisited,
    navigationPreferences,
    
    // Actions
    goBack,
    goToPage,
    addToQuickAccess,
    removeFromQuickAccess,
    updateNavigationPreferences,
    
    // Utilities
    getPageTitle,
    getNavigationSuggestions,
    
    // Current page info
    currentPath: location.pathname,
    currentTitle: getPageTitle(location.pathname)
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export { NavigationProvider, useNavigation };
export default NavigationProvider;
